import React, { useEffect, useState } from "react";
import { api, fmtDateTime, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  PencilSimple,
  Trash,
  UsersThree,
  Buildings,
  Key,
  Copy,
  CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { confirmToast } from "@/utils/toastConfirm";

const Admin = () => {
  return (
    <div data-testid="page-admin">
      <PageHeader
        title="Administração"
        subtitle="Gerencie grupos de clientes (ex: contadores, holdings) e usuários com acesso ao painel. Cada grupo agrupa um conjunto de empresas/CNPJs."
      />
      <div className="px-4 md:px-8 py-6">
        <Tabs defaultValue="groups" className="w-full min-w-0">
          <div className="w-full overflow-x-auto scrollbar-thin pb-2 mb-2 md:pb-0 md:mb-0">
            <TabsList
              data-testid="admin-tabs"
              className="inline-flex w-max min-w-full justify-start"
            >
              <TabsTrigger
                value="groups"
                data-testid="tab-groups"
                className="flex-shrink-0"
              >
                <Buildings size={14} className="mr-1.5" /> Grupos
              </TabsTrigger>
              <TabsTrigger
                value="users"
                data-testid="tab-users"
                className="flex-shrink-0"
              >
                <UsersThree size={14} className="mr-1.5" /> Usuários
              </TabsTrigger>
              <TabsTrigger
                value="api-keys"
                data-testid="tab-api-keys"
                className="flex-shrink-0"
              >
                <Key size={14} className="mr-1.5" /> API Keys
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="groups" className="mt-4 md:mt-6">
            <GroupsTab />
          </TabsContent>
          <TabsContent value="users" className="mt-4 md:mt-6">
            <UsersTab />
          </TabsContent>
          <TabsContent value="api-keys" className="mt-4 md:mt-6">
            <ApiKeysTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// =============== GROUPS ===============
const GroupsTab = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nome: "", descricao: "" });
  const [submitting, setSubmitting] = useState(false);
  const [tenantsModal, setTenantsModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/groups");
      setGroups(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ nome: "", descricao: "" });
    setOpen(true);
  };
  const openEdit = (g) => {
    setEditing(g);
    setForm({ nome: g.nome, descricao: g.descricao || "" });
    setOpen(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (editing) {
        await api.patch(`/admin/groups/${editing.id}`, form);
        toast.success("Grupo atualizado");
      } else {
        await api.post("/admin/groups", form);
        toast.success("Grupo criado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Erro ao salvar",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const remove = (g) => {
    confirmToast(
      `Excluir o grupo "${g.nome}"?`,
      async () => {
        try {
          await api.delete(`/admin/groups/${g.id}`);
          toast.success("Grupo excluído");
          load();
        } catch (e) {
          toast.error(
            formatApiErrorDetail(e?.response?.data?.detail) || "Erro",
          );
        }
      },
      "Excluir",
      true,
    );
  };

  return (
    <>
      <div className="flex flex-col lg:flex-row gap-2 items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold tracking-tight">
            Grupos de Clientes
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Cada grupo agrupa empresas e usuários — ex:{" "}
            <span className="font-mono">contador-silva</span>,{" "}
            <span className="font-mono">holding-x</span>.
          </p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="group-create-btn"
          className="rounded-sm"
        >
          <Plus size={16} /> Novo Grupo
        </Button>
      </div>
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="data-table" data-testid="groups-table">
          <thead>
            <tr>
              <th className="text-left">Nome</th>
              <th className="text-left">Descrição</th>
              <th className="text-center w-24">Empresas</th>
              <th className="text-center w-24">Usuários</th>
              <th className="text-left w-36">Criado em</th>
              <th className="text-right w-20">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={6}
                  className="text-center py-8 text-muted-foreground"
                >
                  Carregando...
                </td>
              </tr>
            )}
            {!loading && groups.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="text-center py-12 text-muted-foreground"
                >
                  Nenhum grupo criado.
                </td>
              </tr>
            )}
            {groups.map((g) => (
              <tr key={g.id} data-testid={`group-row-${g.id}`}>
                <td className="font-medium text-left">{g.nome}</td>
                <td className="text-sm text-muted-foreground text-left">
                  {g.descricao || "—"}
                </td>
                <td className="text-center">
                  <button
                    onClick={() => setTenantsModal(g)}
                    className="font-mono text-sm hover:underline text-primary"
                    data-testid={`group-tenants-${g.id}`}
                  >
                    {g.tenant_count}
                  </button>
                </td>
                <td className="text-center font-mono">{g.user_count}</td>
                <td className="text-xs text-left">
                  {fmtDateTime(g.created_at)}
                </td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => openEdit(g)}
                      className="p-1.5 rounded-sm hover:bg-muted"
                      data-testid={`group-edit-${g.id}`}
                      title="Editar"
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      onClick={() => remove(g)}
                      className="p-1.5 rounded-sm hover:bg-muted text-destructive"
                      data-testid={`group-delete-${g.id}`}
                      title="Excluir"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="w-[95vw] sm:w-full"
          data-testid="group-dialog"
        >
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Grupo" : "Novo Grupo"}</DialogTitle>
            <DialogDescription>
              Grupos isolam dados — usuários só veem empresas/NFe do seu grupo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="g-nome">Nome</Label>
              <Input
                id="g-nome"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className="mt-1.5"
                data-testid="group-form-nome"
                placeholder="ex: contador-silva, holding-acme"
              />
            </div>
            <div>
              <Label htmlFor="g-desc">Descrição</Label>
              <Textarea
                id="g-desc"
                value={form.descricao}
                onChange={(e) =>
                  setForm({ ...form, descricao: e.target.value })
                }
                className="mt-1.5"
                data-testid="group-form-desc"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="group-form-submit"
            >
              {submitting ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GroupTenantsDialog
        group={tenantsModal}
        onClose={() => {
          setTenantsModal(null);
          load();
        }}
      />
    </>
  );
};

const GroupTenantsDialog = ({ group, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [allTenants, setAllTenants] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!group) return;
    (async () => {
      setLoading(true);
      try {
        const [d, all] = await Promise.all([
          api.get(`/admin/groups/${group.id}`),
          api.get(`/tenants`),
        ]);
        setDetail(d.data);
        setAllTenants(all.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [group]);

  const link = async (tenantId) => {
    try {
      await api.post(`/admin/groups/${group.id}/tenants/${tenantId}`);
      toast.success("Empresa vinculada");
      const d = await api.get(`/admin/groups/${group.id}`);
      setDetail(d.data);
      const all = await api.get(`/tenants`);
      setAllTenants(all.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erro");
    }
  };

  const unlink = async (tenantId) => {
    try {
      await api.delete(`/admin/groups/${group.id}/tenants/${tenantId}`);
      toast.success("Vínculo removido");
      const d = await api.get(`/admin/groups/${group.id}`);
      setDetail(d.data);
      const all = await api.get(`/tenants`);
      setAllTenants(all.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erro");
    }
  };

  if (!group) return null;
  const linked = detail?.tenants || [];
  const unlinked = allTenants.filter((t) => t.group_id !== group.id);

  return (
    <Dialog open={!!group} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="w-[95vw] sm:w-full max-w-2xl"
        data-testid="group-tenants-dialog"
      >
        <DialogHeader>
          <DialogTitle>Empresas do grupo "{group.nome}"</DialogTitle>
          <DialogDescription>
            Vincule ou desvincule CNPJs deste grupo.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-muted-foreground">
            Carregando...
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="label-overline mb-2">
                Vinculados ({linked.length})
              </div>
              <div className="space-y-1.5 max-h-96 overflow-y-auto scrollbar-thin">
                {linked.length === 0 && (
                  <div className="text-xs text-muted-foreground">
                    Nenhuma empresa vinculada.
                  </div>
                )}
                {linked.map((t) => (
                  <div
                    key={t.id}
                    className="border border-border rounded-sm p-2 flex items-center justify-between text-sm"
                  >
                    <div>
                      <div className="font-medium">{t.razao_social}</div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {t.cnpj}
                      </div>
                    </div>
                    <button
                      onClick={() => unlink(t.id)}
                      className="text-destructive text-xs hover:underline"
                    >
                      remover
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="label-overline mb-2">
                Disponíveis ({unlinked.length})
              </div>
              <div className="space-y-1.5 max-h-96 overflow-y-auto scrollbar-thin">
                {unlinked.length === 0 && (
                  <div className="text-xs text-muted-foreground">
                    Todas as empresas já estão em algum grupo.
                  </div>
                )}
                {unlinked.map((t) => (
                  <div
                    key={t.id}
                    className="border border-border rounded-sm p-2 flex items-center justify-between text-sm"
                  >
                    <div>
                      <div className="font-medium">{t.razao_social}</div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {t.cnpj}{" "}
                        {t.group_id && (
                          <span className="ml-1 text-amber-600">
                            · em outro grupo
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => link(t.id)}
                      className="text-primary text-xs hover:underline"
                    >
                      vincular
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============== USERS ===============
const emptyUser = {
  email: "",
  name: "",
  password: "",
  role: "group_user",
  user_type: "escritorio_contabil",
  group_id: "",
  tenant_ids: [],
  ativo: true,
};

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [allTenants, setAllTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUser);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, g, t] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/groups"),
        api.get("/tenants"),
      ]);
      setUsers(u.data);
      setGroups(g.data);
      setAllTenants(t.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyUser);
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      name: u.name,
      password: "",
      role: u.role,
      user_type: u.user_type || "escritorio_contabil",
      group_id: u.group_id || "",
      tenant_ids: u.tenant_ids || [],
      ativo: u.ativo,
    });
    setOpen(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (editing) {
        const payload = {
          name: form.name,
          role: form.role,
          user_type: form.user_type,
          group_id:
            form.role === "platform_admin" ? null : form.group_id || null,
          tenant_ids: form.user_type === "empresa" ? form.tenant_ids : [],
          ativo: form.ativo,
        };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/users/${editing.id}`, payload);
        toast.success("Usuário atualizado");
      } else {
        const payload = {
          email: form.email.trim().toLowerCase(),
          name: form.name,
          password: form.password,
          role: form.role,
          user_type: form.user_type,
          group_id: form.role === "platform_admin" ? null : form.group_id,
          tenant_ids: form.user_type === "empresa" ? form.tenant_ids : [],
          ativo: form.ativo,
        };
        await api.post("/admin/users", payload);
        toast.success("Usuário criado");
      }
      setOpen(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((x) => x.msg).join(" ")
        : detail || "Erro ao salvar";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = (u) => {
    confirmToast(
      `Excluir o usuário ${u.email}?`,
      async () => {
        try {
          await api.delete(`/admin/users/${u.id}`);
          toast.success("Usuário excluído");
          load();
        } catch (e) {
          toast.error(
            formatApiErrorDetail(e?.response?.data?.detail) || "Erro",
          );
        }
      },
      "Excluir",
      true,
    );
  };

  const toggleTenant = (tid) => {
    setForm((f) => ({
      ...f,
      tenant_ids: f.tenant_ids.includes(tid)
        ? f.tenant_ids.filter((x) => x !== tid)
        : [...f.tenant_ids, tid],
    }));
  };

  const groupTenants = allTenants.filter((t) => t.group_id === form.group_id);

  return (
    <>
      <div className="flex flex-col lg:flex-row gap-2 items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold tracking-tight">Usuários</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Apenas o administrador da plataforma cria contas.{" "}
            <strong>Escritório Contábil</strong> vê todas as empresas do grupo;{" "}
            <strong>Empresa</strong> vê apenas empresas explicitamente
            vinculadas.
          </p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="user-create-btn"
          className="rounded-sm"
        >
          <Plus size={16} /> Novo Usuário
        </Button>
      </div>
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="data-table" data-testid="users-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Nome</th>
              <th>Tipo de Acesso</th>
              <th>Grupo</th>
              <th>Empresas</th>
              <th>Status</th>
              <th>Criado em</th>
              <th className="text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={8}
                  className="text-center py-8 text-muted-foreground"
                >
                  Carregando...
                </td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="text-center py-12 text-muted-foreground"
                >
                  Nenhum usuário.
                </td>
              </tr>
            )}
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="font-mono text-xs">{u.email}</td>
                <td>{u.name}</td>
                <td>
                  {u.role === "platform_admin" ? (
                    <StatusBadge variant="info">Administrador</StatusBadge>
                  ) : u.user_type === "empresa" ? (
                    <StatusBadge variant="warning">Empresa</StatusBadge>
                  ) : (
                    <StatusBadge variant="success">Esc. Contábil</StatusBadge>
                  )}
                </td>
                <td className="text-sm">
                  {groups.find((g) => g.id === u.group_id)?.nome ||
                    (u.role === "platform_admin" ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <span className="text-amber-600 text-xs">Sem grupo</span>
                    ))}
                </td>
                <td className="text-xs">
                  {u.role === "platform_admin"
                    ? "Todos"
                    : u.user_type === "empresa"
                      ? `${(u.tenant_ids || []).length} vinculada(s)`
                      : "Todos do grupo"}
                </td>
                <td>
                  {u.ativo ? (
                    <StatusBadge variant="success">Ativo</StatusBadge>
                  ) : (
                    <StatusBadge variant="neutral">Inativo</StatusBadge>
                  )}
                </td>
                <td className="text-xs">{fmtDateTime(u.created_at)}</td>
                <td>
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => openEdit(u)}
                      className="p-1.5 rounded-sm hover:bg-muted"
                      data-testid={`user-edit-${u.id}`}
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      onClick={() => remove(u)}
                      className="p-1.5 rounded-sm hover:bg-muted text-destructive"
                      data-testid={`user-delete-${u.id}`}
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="user-dialog"
          className="w-[95vw] lg:w-full max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-thin"
        >
          <DialogHeader>
            <DialogTitle>
              {editing ? "Editar Usuário" : "Novo Usuário"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "Deixe a senha em branco para manter a atual."
                : "O usuário receberá email + senha temporária. Mínimo 8 caracteres com letras e números."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Nome</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5"
                data-testid="user-form-name"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 font-mono"
                data-testid="user-form-email"
              />
            </div>

            <div>
              <Label>
                Senha{" "}
                {editing && (
                  <span className="text-xs text-muted-foreground">
                    (opcional)
                  </span>
                )}
              </Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5"
                data-testid="user-form-password"
                placeholder="mín. 8 caracteres"
              />
            </div>
            <div>
              <Label>Papel</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
              >
                <SelectTrigger data-testid="user-form-role" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="group_user">Usuário de Grupo</SelectItem>
                  <SelectItem value="platform_admin">
                    Administrador da Plataforma
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>
                Grupo{" "}
                {form.role === "group_user" && (
                  <span className="text-destructive">*</span>
                )}
              </Label>
              <Select
                value={form.group_id || "none"}
                onValueChange={(v) =>
                  setForm({
                    ...form,
                    group_id: v === "none" ? "" : v,
                    tenant_ids: [],
                  })
                }
                disabled={form.role === "platform_admin"}
              >
                <SelectTrigger data-testid="user-form-group" className="mt-1.5">
                  <SelectValue placeholder="Selecionar grupo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— sem grupo —</SelectItem>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.role === "group_user" ? (
              <div>
                <Label>Tipo de Acesso</Label>
                <div
                  className="grid grid-cols-2 gap-2 mt-1.5"
                  data-testid="user-form-type"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        user_type: "escritorio_contabil",
                        tenant_ids: [],
                      })
                    }
                    className={[
                      "border rounded-sm p-2.5 text-left transition-colors",
                      form.user_type === "escritorio_contabil"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted",
                    ].join(" ")}
                    data-testid="type-escritorio"
                  >
                    <div className="text-sm font-semibold">Contábil</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
                      Vê todas as empresas.
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, user_type: "empresa" })}
                    className={[
                      "border rounded-sm p-2.5 text-left transition-colors",
                      form.user_type === "empresa"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted",
                    ].join(" ")}
                    data-testid="type-empresa"
                  >
                    <div className="text-sm font-semibold">Empresa</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
                      Vê apenas vinculadas.
                    </div>
                  </button>
                </div>
              </div>
            ) : (
              <div className="hidden md:block"></div>
            )}

            {form.role === "group_user" && form.user_type === "empresa" && (
              <div className="col-span-1 md:col-span-2">
                <Label>
                  Empresas vinculadas{" "}
                  {form.group_id ? "" : "(selecione um grupo primeiro)"}
                </Label>
                <div
                  className="mt-1.5 border border-border rounded-sm max-h-40 overflow-y-auto scrollbar-thin"
                  data-testid="user-form-tenants"
                >
                  {!form.group_id && (
                    <div className="p-3 text-xs text-muted-foreground">
                      Selecione um grupo para listar empresas.
                    </div>
                  )}
                  {form.group_id && groupTenants.length === 0 && (
                    <div className="p-3 text-xs text-muted-foreground">
                      Nenhuma empresa nesse grupo.
                    </div>
                  )}
                  {groupTenants.map((t) => (
                    <label
                      key={t.id}
                      className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-b-0 hover:bg-muted/30 cursor-pointer text-sm"
                      data-testid={`tenant-checkbox-${t.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={form.tenant_ids.includes(t.id)}
                        onChange={() => toggleTenant(t.id)}
                        className="rounded-sm"
                      />
                      <div className="flex-1">
                        <div className="font-medium">{t.razao_social}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">
                          {t.cnpj}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                {form.user_type === "empresa" &&
                  form.tenant_ids.length === 0 &&
                  form.group_id && (
                    <p className="text-xs text-amber-600 mt-1">
                      ⚠ Nenhuma empresa selecionada — usuário não verá nada até
                      vincular pelo menos uma.
                    </p>
                  )}
              </div>
            )}

            <div className="col-span-1 md:col-span-2 flex items-center gap-3 pt-2">
              <Switch
                checked={form.ativo}
                onCheckedChange={(v) => setForm({ ...form, ativo: v })}
                data-testid="user-form-ativo"
              />
              <Label className="cursor-pointer">Conta ativa</Label>
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="user-form-submit"
            >
              {submitting ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// =============== API KEYS ===============
const ApiKeysTab = () => {
  const [keys, setKeys] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", group_id: "", cnpjs: "" });
  const [submitting, setSubmitting] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [k, g] = await Promise.all([
        api.get("/admin/api-keys"),
        api.get("/admin/groups"),
      ]);
      setKeys(k.data);
      setGroups(g.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const cnpjs = form.cnpjs
        .split(",")
        .map((s) => s.trim().replace(/\D/g, ""))
        .filter((s) => s.length === 14);
      const r = await api.post("/admin/api-keys", {
        name: form.name,
        group_id: form.group_id,
        cnpjs,
      });
      setCreatedKey(r.data);
      setOpen(false);
      setForm({ name: "", group_id: "", cnpjs: "" });
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((x) => x.msg).join(" ")
        : detail || "Erro";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = async (k) => {
    try {
      await api.patch(`/admin/api-keys/${k.id}?ativo=${!k.ativo}`);
      load();
    } catch {
      toast.error("Erro");
    }
  };

  const remove = (k) => {
    confirmToast(
      `Revogar a API key "${k.name}"?`,
      async () => {
        try {
          await api.delete(`/admin/api-keys/${k.id}`);
          toast.success("API key revogada");
          load();
        } catch {
          toast.error("Erro");
        }
      },
      "Revogar",
      true,
    );
  };

  const copyKey = async () => {
    if (!createdKey?.key) return;
    await navigator.clipboard.writeText(createdKey.key);
    setCopied(true);
    toast.success("Chave copiada para a área de transferência");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <div className="flex flex-col lg:flex-row gap-2 items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold tracking-tight">
            API Keys (acesso desktop / ERP)
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Cada API Key dá acesso somente-leitura aos XMLs de um grupo (e
            opcionalmente CNPJs específicos), via header{" "}
            <span className="font-mono">X-API-Key</span>.
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          data-testid="apikey-create-btn"
          className="rounded-sm"
        >
          <Plus size={16} /> Nova API Key
        </Button>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="data-table" data-testid="apikeys-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Prefixo</th>
              <th>Grupo</th>
              <th>CNPJs</th>
              <th>Status</th>
              <th>Usos</th>
              <th>Último uso</th>
              <th className="text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={8}
                  className="text-center py-8 text-muted-foreground"
                >
                  Carregando...
                </td>
              </tr>
            )}
            {!loading && keys.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="text-center py-12 text-muted-foreground"
                >
                  Nenhuma API Key criada.
                </td>
              </tr>
            )}
            {keys.map((k) => (
              <tr key={k.id} data-testid={`apikey-row-${k.id}`}>
                <td className="font-medium">{k.name}</td>
                <td className="font-mono text-xs">{k.prefix}…</td>
                <td className="text-sm">
                  {groups.find((g) => g.id === k.group_id)?.nome || "—"}
                </td>
                <td className="text-xs">
                  {k.cnpjs?.length
                    ? `${k.cnpjs.length} CNPJ(s)`
                    : "Todos do grupo"}
                </td>
                <td>
                  <button
                    onClick={() => toggle(k)}
                    data-testid={`apikey-toggle-${k.id}`}
                  >
                    <StatusBadge variant={k.ativo ? "success" : "neutral"}>
                      {k.ativo ? "Ativa" : "Inativa"}
                    </StatusBadge>
                  </button>
                </td>
                <td className="font-mono">{k.use_count}</td>
                <td className="text-xs">
                  {k.last_used_at ? fmtDateTime(k.last_used_at) : "—"}
                </td>
                <td>
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => remove(k)}
                      className="p-1.5 rounded-sm hover:bg-muted text-destructive"
                      data-testid={`apikey-delete-${k.id}`}
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 bg-muted/30 border border-border rounded-sm p-4 text-xs">
        <div className="label-overline mb-2">
          Como o sistema desktop / ERP usa essa API
        </div>
        <div className="space-y-1.5 text-muted-foreground">
          <div>
            1. <strong>Listar NF-e do CNPJ no período</strong>:
          </div>
          <pre className="bg-white border border-border rounded-sm p-2 font-mono text-[11px] overflow-x-auto">
            curl -H "X-API-Key: uk_..." \ "{API_BASE_URL}
            /v1/nfe?cnpj=12345678000190&date_from=2026-04-01&date_to=2026-04-30"
          </pre>
          <div className="mt-2">
            2. <strong>Baixar XML único</strong>:
          </div>
          <pre className="bg-white border border-border rounded-sm p-2 font-mono text-[11px] overflow-x-auto">
            curl -H "X-API-Key: uk_..." \ "{API_BASE_URL}/v1/nfe/{`{chave}`}
            /xml" -o nota.xml
          </pre>
          <div className="mt-2">
            3. <strong>Baixar ZIP por período</strong>:
          </div>
          <pre className="bg-white border border-border rounded-sm p-2 font-mono text-[11px] overflow-x-auto">
            curl -H "X-API-Key: uk_..." \ "{API_BASE_URL}
            /v1/nfe/bulk?cnpj=12345678000190&date_from=2026-04-01" -o lote.zip
          </pre>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="w-[95vw] sm:w-full"
          data-testid="apikey-dialog"
        >
          <DialogHeader>
            <DialogTitle>Nova API Key</DialogTitle>
            <DialogDescription>
              A chave será exibida APENAS UMA VEZ após a criação. Copie e
              armazene com segurança.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="ex: ERP Cliente X · Sistema Desktop"
                className="mt-1.5"
                data-testid="apikey-form-name"
              />
            </div>
            <div>
              <Label>Grupo</Label>
              <Select
                value={form.group_id}
                onValueChange={(v) => setForm({ ...form, group_id: v })}
              >
                <SelectTrigger
                  className="mt-1.5"
                  data-testid="apikey-form-group"
                >
                  <SelectValue placeholder="Selecionar grupo" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>CNPJs permitidos (opcional)</Label>
              <Input
                value={form.cnpjs}
                onChange={(e) => setForm({ ...form, cnpjs: e.target.value })}
                placeholder="12345678000190, 98765432000111"
                className="mt-1.5 font-mono"
                data-testid="apikey-form-cnpjs"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Separados por vírgula. Em branco = todos os CNPJs do grupo.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || !form.name || !form.group_id}
              data-testid="apikey-form-submit"
            >
              {submitting ? "Gerando..." : "Gerar API Key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show created key once */}
      <Dialog
        open={!!createdKey}
        onOpenChange={(v) => !v && setCreatedKey(null)}
      >
        <DialogContent
          className="w-[95vw] sm:max-w-md rounded-lg"
          data-testid="apikey-created-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle
                size={20}
                weight="duotone"
                className="text-green-600 shrink-0"
              />{" "}
              API Key gerada
            </DialogTitle>
            <DialogDescription className="text-destructive leading-snug">
              ⚠ A chave abaixo não será mostrada novamente. Copie agora!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div
              className="bg-black text-green-300 font-mono text-xs p-3 rounded-sm break-all"
              data-testid="apikey-value"
            >
              {createdKey?.key}
            </div>
            <Button
              onClick={copyKey}
              className="w-full flex items-center justify-center gap-2"
              data-testid="apikey-copy-btn"
            >
              {copied ? (
                <>
                  <CheckCircle size={16} /> Copiado
                </>
              ) : (
                <>
                  <Copy size={16} /> Copiar chave
                </>
              )}
            </Button>
            <div className="text-xs text-muted-foreground bg-muted/40 p-3 rounded-sm border border-border">
              <strong>Use no header HTTP:</strong>
              {/* O segredo para não estourar a tela no mobile está nestas classes abaixo */}
              <pre className="font-mono mt-1 whitespace-pre-wrap break-all text-[11px]">
                X-API-Key: {createdKey?.key}
              </pre>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setCreatedKey(null)}
              data-testid="apikey-created-close"
              className="w-full sm:w-auto"
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const API_BASE_URL =
  typeof window !== "undefined" ? `${window.location.origin}/api` : "/api";

export default Admin;
